import React, { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Award, Leaf, BadgeCheck } from "lucide-react";



const certIcons = [ShieldCheck, Award, Leaf, BadgeCheck];
const certKeys = ["certHalal", "certHygiene", "certQuality", "certOrganic"];
const HALAL_CERT_IMAGE = "/images/halal-certificate.png";
export default function About() {
  const { t, isRTL } = useLanguage();
  const [expandedCert, setExpandedCert] = useState(null);

  return (
    <section id="about" data-testid="about-section" className="py-20 lg:py-28 bg-[#F9F9F9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2
            data-testid="about-title"
            className="font-heading text-3xl lg:text-4xl font-semibold tracking-tight mb-4"
            style={{ color: "#0A0A0A" }}
          >
            {t("about.title")}
          </h2>
          <div className="w-16 h-0.5 mx-auto" style={{ backgroundColor: "#D4AF37" }} />
        </div>

        {/* About Content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 mb-20">
          {/* Text Content */}
          <div className="lg:col-span-3 space-y-5">
            <h3
              className="font-heading text-2xl font-medium"
              style={{ color: "#8B0000" }}
            >
              {t("about.subtitle")}
            </h3>
            <p className="text-base leading-relaxed text-neutral-600">
              {t("about.text1")}
            </p>
            <p className="text-base leading-relaxed text-neutral-600">
              {t("about.text2")}
            </p>
            <p className="text-base leading-relaxed text-neutral-600">
              {t("about.text3")}
            </p>
          </div>

          
          {/* Quote Block */}
<div className="lg:col-span-2 flex justify-center lg:justify-end">
  <div
    className="relative w-full min-h-[420px] rounded-sm flex flex-col justify-center p-10 overflow-hidden"
    style={{ backgroundColor: "#FAF6EF", border: "1px solid #E8DCC8" }}
  >
    {/* Faded calligraphy background word */}
    <span
      className="absolute inset-0 flex items-center justify-center select-none pointer-events-none"
      style={{
  fontFamily: "'Amiri', serif",
  color: "#D4AF37",
  opacity: 0.18,       // was 0.1, now more visible
  fontSize: "12rem",   // was 13rem, now bigger
  lineHeight: 1,
}}
      aria-hidden="true"
    >
      الجودة
    </span>

    {/* Gold opening quotation mark */}
    <span
      className="text-8xl leading-none mb-2 self-start z-10"
      style={{ color: "#D4AF37", fontFamily: "Georgia, serif" }}
    >
      ❝
    </span>

    {/* Quote text */}
    <p
  className="relative z-10 italic text-center leading-loose"
  style={{
    color: "#3A2A1A",
    fontFamily: "'Amiri', serif",
    fontSize: "1.7rem",
    fontWeight: "700",
    wordBreak: "break-word",
    whiteSpace: "normal",
  }}
>
  {t("about.quote")}
</p>

    {/* Gold closing quotation mark */}
    <span
      className="text-8xl leading-none mt-2 self-end z-10"
      style={{ color: "#D4AF37", fontFamily: "Georgia, serif" }}
    >
      ❞
    </span>

    {/* Attribution */}
    <p
      className="mt-4 text-sm uppercase tracking-widest text-center z-10"
      style={{ color: "#D4AF37", fontWeight: "600" }}
    >
      — Al-Kasbah
    </p>

    {/* Decorative corner borders */}
    <div
      className="absolute bottom-3 right-3 w-12 h-12"
      style={{ borderBottom: "2px solid #D4AF37", borderRight: "2px solid #D4AF37" }}
    />
    <div
      className="absolute top-3 left-3 w-12 h-12"
      style={{ borderTop: "2px solid #D4AF37", borderLeft: "2px solid #D4AF37" }}
    />
  </div>
</div></div>

        {/* Features & Certification Carousel */}
<div>
  <h3
    data-testid="about-certificates-title"
    className="font-heading text-2xl font-medium text-center mb-10"
    style={{ color: "#0A0A0A" }}
  >
    {t("about.certificates")}
  </h3>

  <div className="max-w-4xl mx-auto px-14">
    <Carousel
  opts={{ align: "start", loop: true, direction: isRTL ? "rtl" : "ltr" }}
  className="w-full"
  dir={isRTL ? "rtl" : "ltr"}
>
      <CarouselContent>
        {certKeys.map((key, index) => {
          const Icon = certIcons[index];
          const isHalal = key === "certHalal";

          return (
            <CarouselItem key={key} className="basis-full sm:basis-1/2 lg:basis-1/3">
              {isHalal ? (
                // Halal — clickable with "Click to view"
                <button
                  data-testid={`cert-${key}`}
                  onClick={() => setExpandedCert(index)}
                  className="w-full h-full p-6 bg-white rounded-sm border border-neutral-100 hover:shadow-md transition-shadow text-center cursor-pointer"
                >
                  <div
                    className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full"
                    style={{ backgroundColor: "#8B0000" }}
                  >
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-2" style={{ color: "#0A0A0A" }}>
                    {t(`about.${key}`)}
                  </h4>
                  <p className="text-xs text-neutral-400">{t("about.certClickToView")}</p>
                </button>
              ) : (
                // Others — plain info card, no click
                <div
                  data-testid={`cert-${key}`}
                  className="w-full h-full p-6 bg-white rounded-sm border border-neutral-100 text-center"
                >
                  <div
                    className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full"
                    style={{ backgroundColor: "#8B0000" }}
                  >
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-2" style={{ color: "#0A0A0A" }}>
                    {t(`about.${key}`)}
                  </h4>
                  <p className="text-xs text-neutral-500 leading-relaxed">
                    {t(`about.${key}Desc`)}
                  </p>
                </div>
              )}
            </CarouselItem>
          );
        })}
      </CarouselContent>
      <CarouselPrevious className="border-neutral-200" />
      <CarouselNext className="border-neutral-200" />
    </Carousel>
  </div>
</div>
</div>
{/* Certificate Dialog - Halal Only */}
<Dialog open={expandedCert === 0} onOpenChange={() => setExpandedCert(null)}>
  <DialogContent className="max-w-lg">
    <DialogTitle className="font-heading font-semibold text-lg" style={{ color: "#0A0A0A" }}>
      {t("about.certHalal")}
    </DialogTitle>
    <p className="text-sm text-neutral-500">Halal Certification Document</p>
   <img
  src={HALAL_CERT_IMAGE}
  alt={t("about.certHalal")}
  className="w-full h-auto object-contain rounded-sm mt-2"
/>
  </DialogContent>
</Dialog>
    </section>
  );
}
